import { multiColumnSort } from "./functions";

/** Same click cycle as tableBarChart.js: asc → desc → clear. */
export function cycleMappingSortState(sortState, field) {
  const next = Array.isArray(sortState) ? [...sortState] : [];
  const idx = next.findIndex((s) => s && s.field === field);
  if (idx < 0) {
    next.push({ field, direction: "asc" });
  } else if (next[idx].direction === "asc") {
    next[idx] = { field, direction: "desc" };
  } else {
    next.splice(idx, 1);
  }
  return next;
}

function rowSortKey(field, mode) {
  if (mode === "table") {
    if (field === "columnName") return "columnName";
    if (field === "displayName") return "displayName";
  }
  if (field === "columnName") return "expr";
  if (field === "displayName") return "label";
  return field;
}

/**
 * Sort row order without cloning rows (keeps object identity for row-change handlers).
 * @param {object[]} rows
 * @param {{ field: string, direction: 'asc'|'desc' }[]} sortState
 * @param {'table'|'sql'} mode
 */
function normalizeDisplayKey(value) {
  return String(value || "").trim().toLowerCase();
}

/** Rank for included rows: chip order first, then catalog seq. */
function includedRowRank(row, visibleColumns) {
  const displayKey = normalizeDisplayKey(row.displayName);
  const catalogKey = normalizeDisplayKey(row.columnName);
  if (Array.isArray(visibleColumns) && visibleColumns.length) {
    for (let i = 0; i < visibleColumns.length; i++) {
      const chip = visibleColumns[i];
      if (!chip) continue;
      const chipBase = normalizeDisplayKey(chip.base);
      const chipCatalog = normalizeDisplayKey(chip.catalogKey);
      if (
        chipBase === displayKey ||
        chipBase === catalogKey ||
        chipCatalog === catalogKey ||
        chipCatalog === displayKey
      ) {
        return i;
      }
    }
    return 5000 + (row.seq || 0);
  }
  return row.seq || 0;
}

/**
 * Table mode: included (checked) rows first, then unchecked.
 * Included rows follow visible chip order when provided.
 */
export function partitionIncludedMappingRowsFirst(
  rows,
  mode,
  visibleColumns = null,
) {
  const list = rows || [];
  if (mode !== "table" || !list.length) return list;

  return [...list]
    .map((row, index) => ({
      row,
      index,
      included: !!(row && row.include),
      rank: row && row.include ? includedRowRank(row, visibleColumns) : 0,
    }))
    .sort((a, b) => {
      if (a.included !== b.included) return a.included ? -1 : 1;
      if (a.included && b.included && a.rank !== b.rank) {
        return a.rank - b.rank;
      }
      return a.index - b.index;
    })
    .map((entry) => entry.row);
}

/** Rank for shown SQL rows: visible chip order first. */
function sqlShownRowRank(row, visibleColumns) {
  const displayKey = normalizeDisplayKey(row.label);
  if (Array.isArray(visibleColumns) && visibleColumns.length) {
    for (let i = 0; i < visibleColumns.length; i++) {
      const chip = visibleColumns[i];
      if (!chip) continue;
      if (normalizeDisplayKey(chip.base) === displayKey) {
        return i;
      }
    }
    return 5000;
  }
  return 0;
}

/** SQL mode: shown (checked) rows first, in visible chip order when provided. */
export function partitionShownSqlMappingRowsFirst(rows, visibleColumns = null) {
  const list = rows || [];
  if (!list.length) return list;

  return [...list]
    .map((row, index) => ({
      row,
      index,
      shown: !!(row && row.show),
      rank: row && row.show ? sqlShownRowRank(row, visibleColumns) : 0,
    }))
    .sort((a, b) => {
      if (a.shown !== b.shown) return a.shown ? -1 : 1;
      if (a.shown && b.shown && a.rank !== b.rank) {
        return a.rank - b.rank;
      }
      return a.index - b.index;
    })
    .map((entry) => entry.row);
}

export function sortMappingRows(rows, sortState, mode, options = {}) {
  const { prioritizeIncluded = false, visibleColumns = null } = options;
  const list = rows || [];
  if (!list.length) return [];

  const wrappers = list.map((row, index) => ({
    row,
    __originalIndex: index,
    columnName: row.columnName,
    displayName: row.displayName,
    expr: row.expr,
    label: row.label,
  }));

  let result;
  if (!sortState || !sortState.length) {
    result = wrappers
      .sort((a, b) => a.__originalIndex - b.__originalIndex)
      .map((w) => w.row);
  } else {
    const columns = sortState.map((s) => rowSortKey(s.field, mode));
    const directions = sortState.map((s) => s.direction);
    result = wrappers
      .sort(multiColumnSort(columns, directions))
      .map((w) => w.row);
  }

  if (prioritizeIncluded && mode === "table") {
    return partitionIncludedMappingRowsFirst(result, mode, visibleColumns);
  }
  if (prioritizeIncluded && mode === "sql") {
    return partitionShownSqlMappingRowsFirst(result, visibleColumns);
  }
  return result;
}

export function mappingSortIconClass(sortState, field) {
  const item = (sortState || []).find((s) => s && s.field === field);
  if (!item) return "fa-sort text-muted";
  return item.direction === "asc" ? "fa-sort-up text-primary" : "fa-sort-down text-primary";
}

export function mappingSortOrderIndex(sortState, field) {
  const idx = (sortState || []).findIndex((s) => s && s.field === field);
  return idx >= 0 ? idx + 1 : null;
}
